import React, { useEffect, useState } from "react";
import axios from "../../../api/axios"; // use your axios instance
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UpdateIcon from "@mui/icons-material/Update";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

export default function TemporaryStoppagesManager() {
  const [stoppages, setStoppages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ department_id: "", reason: "", status: "" });

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editStop, setEditStop] = useState(null);
  const [editForm, setEditForm] = useState({
    reason: "",
    start_date: "",
    expected_resumption_date: "",
    scope: "",
    remarks: "",
  });
  const [editFile, setEditFile] = useState(null);

  // Resume dialog state
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeStop, setResumeStop] = useState(null);
  const [resumeDate, setResumeDate] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeRemarks, setResumeRemarks] = useState("");

  // Extend dialog state
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendStop, setExtendStop] = useState(null);
  const [extendDate, setExtendDate] = useState("");

  // fetch stoppages
  const fetchStoppages = async (params = {}) => {
    setLoading(true);
    try {
      const res = await axios.get("/temporary-stoppages", { params });
      // backend returns an array
      setStoppages(res.data || []);
    } catch (err) {
      console.error("Failed to fetch stoppages:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoppages();
  }, []);

  // FILTERS
  const applyFilters = () => {
    const params = {};
    if (filters.department_id) params.department_id = filters.department_id;
    if (filters.reason) params.reason = filters.reason;
    if (filters.status) params.status = filters.status;
    fetchStoppages(params);
  };

  // UTILS
  const getStaffName = (stop) => {
    const biodata = stop.staff?.biodata;
    if (biodata) return `${biodata.surname || ""} ${biodata.firstname || ""}`.trim();
    // fallback
    return stop.staff?.pfno || "-";
  };

  const badgeFromStop = (stop) => {
    if (stop.badge) return stop.badge;
    if (stop.status === "Resumed") return "✅ Resumed";
    // scheduled if start_date in future
    try {
      const start = new Date(stop.start_date);
      if (start > new Date()) return "⏳ Scheduled Stop";
    } catch (e) {}
    return "🔴 Active Stop";
  };

  const badgeColor = (badge) => {
    if (badge.includes("Resumed")) return "success";
    if (badge.includes("Scheduled")) return "warning";
    return "error";
  };

  // OPEN EDIT
  const openEdit = (stop) => {
    setEditStop(stop);
    setEditForm({
      reason: stop.reason || "",
      start_date: stop.start_date || "",
      expected_resumption_date: stop.expected_resumption_date || "",
      scope: stop.scope || "",
      remarks: stop.remarks || "",
    });
    setEditFile(null);
    setEditOpen(true);
  };

  // SUBMIT EDIT (PUT)
  const submitEdit = async () => {
    if (!editStop) return;
    try {
      // If user selected a file, use FormData with method override _method=PUT
      if (editFile) {
        const fd = new FormData();
        fd.append("_method", "PUT"); // Laravel method override
        fd.append("reason", editForm.reason);
        fd.append("start_date", editForm.start_date);
        fd.append("expected_resumption_date", editForm.expected_resumption_date);
        fd.append("scope", editForm.scope);
        fd.append("remarks", editForm.remarks || "");
        fd.append("authority_document", editFile);
        await axios.post(`/temporary-stoppages/${editStop.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // No file -> simple PUT with JSON
        await axios.put(`/temporary-stoppages/${editStop.id}`, {
          reason: editForm.reason,
          start_date: editForm.start_date,
          expected_resumption_date: editForm.expected_resumption_date,
          scope: editForm.scope,
          remarks: editForm.remarks,
        });
      }

      setEditOpen(false);
      setEditStop(null);
      fetchStoppages();
    } catch (err) {
      console.error("Update failed:", err.response?.data || err);
      alert("Failed to update stoppage. See console for details.");
    }
  };

  // OPEN RESUME
  const openResume = (stop) => {
    setResumeStop(stop);
    setResumeDate("");
    setResumeFile(null);
    setResumeRemarks("");
    setResumeOpen(true);
  };

  // SUBMIT RESUME
  const submitResume = async () => {
    if (!resumeStop) return;
    if (!resumeDate) {
      alert("Please provide actual resumption date.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("actual_resumption_date", resumeDate);
      if (resumeRemarks) fd.append("remarks", resumeRemarks);
      if (resumeFile) fd.append("resumption_authority_document", resumeFile);

      await axios.post(`/temporary-stoppages/${resumeStop.id}/resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResumeOpen(false);
      setResumeStop(null);
      fetchStoppages();
    } catch (err) {
      console.error("Resume failed:", err.response?.data || err);
      alert("Failed to resume stoppage. See console for details.");
    }
  };

  // OPEN EXTEND
  const openExtend = (stop) => {
    setExtendStop(stop);
    setExtendDate("");
    setExtendOpen(true);
  };

  // SUBMIT EXTEND
  const submitExtend = async () => {
    if (!extendStop) return;
    if (!extendDate) {
      alert("Please select a new expected resumption date.");
      return;
    }
    try {
      await axios.post(`/temporary-stoppages/${extendStop.id}/extend`, {
        new_expected_resumption_date: extendDate,
      });
      setExtendOpen(false);
      setExtendStop(null);
      fetchStoppages();
    } catch (err) {
      console.error("Extend failed:", err.response?.data || err);
      alert("Failed to extend stoppage. See console for details.");
    }
  };

  return (
    <Box sx={{ ml: { md: "260px" }, p: 4 }}>
      {" "}
      {/* adjust md ml to match your sidebar width */}
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Manage Temporary Stoppages
      </Typography>
      {/* Filters row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Department ID"
            size="small"
            fullWidth
            value={filters.department_id}
            onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Reason</InputLabel>
            <Select
              label="Reason"
              value={filters.reason}
              onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Suspension">Suspension</MenuItem>
              <MenuItem value="Leave Without Pay">Leave Without Pay</MenuItem>
              <MenuItem value="Study Leave">Study Leave</MenuItem>
              <MenuItem value="Medical Leave">Medical Leave</MenuItem>
              <MenuItem value="Secondment">Secondment</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Resumed">Resumed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3} container alignItems="center" spacing={1}>
          <Grid item>
            <Button variant="contained" onClick={applyFilters}>
              Apply Filters
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({ department_id: "", reason: "", status: "" });
                fetchStoppages();
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table size="medium" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell>
                <b>PF No</b>
              </TableCell>
              {/* <TableCell>
                <b>Name</b>
              </TableCell> */}
              <TableCell>
                <b>Reason</b>
              </TableCell>
              <TableCell>
                <b>Start → Expected</b>
              </TableCell>
              <TableCell>
                <b>Scope</b>
              </TableCell>
              <TableCell>
                <b>Status</b>
              </TableCell>
              <TableCell align="center">
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stoppages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {loading ? "Loading..." : "No stoppages found"}
                </TableCell>
              </TableRow>
            ) : (
              stoppages.map((stop) => (
                <TableRow key={stop.id}>
                  <TableCell>{stop.pfno}</TableCell>
                  {/* <TableCell>{getStaffName(stop) || "-"}</TableCell> */}
                  <TableCell>{stop.reason}</TableCell>
                  <TableCell>
                    {stop.start_date} → {stop.expected_resumption_date}
                  </TableCell>
                  <TableCell>{stop.scope}</TableCell>
                  <TableCell>
                    <Chip
                      label={badgeFromStop(stop)}
                      color={badgeColor(badgeFromStop(stop))}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton size="small" color="primary" onClick={() => openEdit(stop)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => openResume(stop)}
                        disabled={stop.status === "Resumed"}
                      >
                        <RestartAltIcon />
                      </IconButton>
                      <IconButton size="small" color="warning" onClick={() => openExtend(stop)}>
                        <UpdateIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Stoppage
          <IconButton
            aria-label="close"
            onClick={() => setEditOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reason"
                fullWidth
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Scope"
                fullWidth
                value={editForm.scope}
                onChange={(e) => setEditForm({ ...editForm, scope: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editForm.start_date}
                onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected Resumption Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editForm.expected_resumption_date}
                onChange={(e) =>
                  setEditForm({ ...editForm, expected_resumption_date: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                fullWidth
                multiline
                rows={3}
                value={editForm.remarks}
                onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Replace Authority Document
                <input
                  type="file"
                  name="authority_document"
                  hidden
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={(e) => setEditFile(e.target.files[0])}
                />
              </Button>
              {editFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {editFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={submitEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resume Dialog */}
      <Dialog open={resumeOpen} onClose={() => setResumeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resume Staff Early</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Actual Resumption Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={resumeDate}
                onChange={(e) => setResumeDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remarks (optional)"
                fullWidth
                multiline
                rows={2}
                value={resumeRemarks}
                onChange={(e) => setResumeRemarks(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Upload Resumption Document (optional)
                <input
                  type="file"
                  name="resumption_authority_document"
                  hidden
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={(e) => setResumeFile(e.target.files[0])}
                />
              </Button>
              {resumeFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {resumeFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumeOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={submitResume}>
            Resume
          </Button>
        </DialogActions>
      </Dialog>
      {/* Extend Dialog */}
      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Stoppage</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="New Expected Resumption Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={extendDate}
            onChange={(e) => setExtendDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={submitExtend}>
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
